import "./styles.scss";
import { useMemo, useState, useRef, useCallback, useEffect, type ReactNode } from "react";
import {
  Avatar,
  Box,
  Button,
  IconButton,
  Modal,
  Slide,
  Typography,
} from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CloseIcon from "@mui/icons-material/Close";
import MicNoneOutlinedIcon from "@mui/icons-material/MicNoneOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import EmojiEmotionsOutlinedIcon from "@mui/icons-material/EmojiEmotionsOutlined";
import { useUserContext } from "../../hooks/userContext";
import { useMutation } from "@apollo/client";
import { SEND_MESSAGE } from "../../graphql/messages";
import { useFirebaseFileUpload } from "../../hooks/useFirebaseFileUpload";
import { FirebasePaths } from "../../utils/firebase";
import type { MediaFile } from "../../__generated__/types";

const QUICK_TEMPLATES = [
  "Hi! I'd love to discuss working together on my upcoming project.",
  "Your profile really stood out. Could we set up a quick consultation?",
  "Hi there! I'd love to learn more about your design process.",
  "We're a great match. Are you available to chat about next steps?",
];

const TEAM_EMOJIS = [
  "😀",
  "😁",
  "😂",
  "🤣",
  "😊",
  "😉",
  "😍",
  "😘",
  "😎",
  "🤔",
  "😅",
  "😇",
  "🙂",
  "🙃",
  "😮",
  "😢",
  "😭",
  "😡",
  "👍",
  "👎",
  "👏",
  "🙌",
  "🎉",
  "❤️",
  "💯",
];

type FloatingChatWidgetProps = {
  recipientId: string;
  recipientRole: "Designer" | "Homeowner" | "Admin";
  recipientName: string;
  recipientAvatarUrl?: string | null;
  matchId?: string | null;
  isDisabled?: boolean;
  disabledReason?: string | null;
  renderTrigger?: (openChat: () => void) => ReactNode;
  onReady?: (openChat: () => void) => void;
};

export const FloatingChatWidget = ({
  recipientId,
  recipientRole,
  recipientName,
  recipientAvatarUrl,
  matchId,
  isDisabled = false,
  disabledReason = null,
  renderTrigger,
  onReady,
}: FloatingChatWidgetProps) => {
  const { user } = useUserContext();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">(
    "idle"
  );
  const [error, setError] = useState("");
  const [pendingAttachments, setPendingAttachments] = useState<MediaFile[]>([]);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const audioInputRef = useRef<HTMLInputElement | null>(null);

  const [sendMessage] = useMutation(SEND_MESSAGE);
  const { onUpload: uploadFiles, loading: uploadingFiles } =
    useFirebaseFileUpload(FirebasePaths.MESSAGES);
  const blockedReason =
    disabledReason?.trim() || "Messaging is unavailable for this match.";
  const handleOpen = useCallback(() => {
    if (isDisabled) {
      setStatus("error");
      setError(blockedReason);
      return;
    }
    setOpen(true);
  }, [blockedReason, isDisabled]);
  const handleClose = useCallback(() => setOpen(false), []);

  const senderRole = user?.accountType || "";
  const isHidden =
    !user?.id ||
    senderRole === "Admin" ||
    user.id === recipientId ||
    !recipientId;

  const helperText = useMemo(() => {
    if (isDisabled) return blockedReason;
    return `Your message will be sent to ${recipientName}. They'll receive a notification and can respond directly.`;
  }, [blockedReason, isDisabled, recipientName]);

  const canSend = Boolean(message.trim() || pendingAttachments.length);

  useEffect(() => {
    if (onReady) {
      onReady(handleOpen);
    }
  }, [handleOpen, onReady]);

  useEffect(() => {
    if (!isDisabled) return;
    if (open) setOpen(false);
  }, [isDisabled, open]);

  if (isHidden) {
    return null;
  }

  const handleFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const uploaded = await uploadFiles(files);
    if (uploaded.length) {
      setPendingAttachments((prev) => [...prev, ...uploaded]);
    }
  };

  const handleRemoveAttachment = (id?: string | null) => {
    if (!id) return;
    setPendingAttachments((prev) => prev.filter((file) => file?._id !== id));
  };

  const handleEmojiSelect = (emoji: string) => {
    setMessage((prev) => `${prev}${emoji}`.slice(0, 200));
    setEmojiOpen(false);
  };

  const handleTemplateClick = (template: string) => {
    setMessage(template.slice(0, 200));
    setStatus("idle");
  };

  const handleSend = async () => {
    const trimmed = message.trim();
    if (
      uploadingFiles ||
      isDisabled ||
      (!trimmed && pendingAttachments.length === 0) ||
      !user?.id ||
      !senderRole
    ) {
      return;
    }

    setStatus("sending");
    setError("");

    try {
      await sendMessage({
        variables: {
          input: {
            senderId: user.id,
            senderRole,
            recipientId,
            recipientRole,
            body: trimmed,
            attachments: pendingAttachments.map((file) => file._id).filter(Boolean),
            matchId: matchId ?? null,
          },
        },
      });

      setStatus("success");
      setMessage("");
      setPendingAttachments([]);
      setTimeout(() => {
        setOpen(false);
        setStatus("idle");
      }, 1200);
    } catch (err: any) {
      setStatus("error");
      setError(err?.message || "Failed to send message");
    }
  };

  return (
    <>
      {renderTrigger ? (
        renderTrigger(handleOpen)
      ) : (
        <IconButton
          className="floating-chat-trigger"
          aria-label="Open chat"
          onClick={handleOpen}
          disabled={isDisabled}
          title={isDisabled ? blockedReason : undefined}
        >
          <ChatBubbleOutlineIcon />
        </IconButton>
      )}

      <Modal open={open} onClose={handleClose}>
        <Slide direction="up" in={open} mountOnEnter unmountOnExit>
          <Box className="floating-chat-modal">
            <Box className="chat-widget-header">
              <Box className="chat-widget-user">
                <Avatar src={recipientAvatarUrl || undefined} />
                <Box>
                  <Typography variant="h6">Message: {recipientName}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Start a conversation
                  </Typography>
                </Box>
              </Box>
              <IconButton
                aria-label="Close"
                onClick={handleClose}
                className="icon-muted"
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <Box className="chat-widget-section">
              <Typography variant="subtitle2" gutterBottom>
                Quick message templates:
              </Typography>
              <Box className="chat-widget-templates">
                {QUICK_TEMPLATES.map((template) => (
                  <button
                    key={template}
                    type="button"
                    className="chat-widget-template"
                    onClick={() => handleTemplateClick(template)}
                  >
                    {template}
                  </button>
                ))}
              </Box>
            </Box>

            <Box className="chat-widget-section">
              <Typography variant="subtitle2" gutterBottom>
                Or write your own message:
              </Typography>
              {pendingAttachments.length > 0 && (
                <Box className="chat-widget-attachment-row">
                  {pendingAttachments.map((file) => (
                    <Box key={file._id} className="chat-widget-attachment-chip">
                      <span className="chat-widget-attachment-name">
                        {file.title || file.name || "Attachment"}
                      </span>
                      <button
                        type="button"
                        className="chat-widget-attachment-remove"
                        onClick={() => handleRemoveAttachment(file._id)}
                        aria-label="Remove attachment"
                      >
                        ×
                      </button>
                    </Box>
                  ))}
                </Box>
              )}
              <textarea
                className="chat-widget-textarea"
                value={message}
                onChange={(event) => {
                  setMessage(event.target.value.slice(0, 200));
                  if (status !== "sending") setStatus("idle");
                }}
                onKeyDown={(event) => {
                  if ((event.nativeEvent as any)?.isComposing) return;
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    if (canSend && status !== "sending" && !uploadingFiles) {
                      handleSend();
                    }
                  }
                }}
                maxLength={200}
                placeholder="Write your message here..."
              />
              <Box className="chat-widget-actions">
                <Box className="chat-widget-action-group">
                  <IconButton
                    aria-label="Attach audio"
                    onClick={() => audioInputRef.current?.click()}
                    className="icon-muted"
                  >
                    <MicNoneOutlinedIcon />
                  </IconButton>
                  <IconButton
                    aria-label="Attach image"
                    onClick={() => imageInputRef.current?.click()}
                    className="icon-muted"
                  >
                    <ImageOutlinedIcon />
                  </IconButton>
                  <IconButton
                    aria-label="Add emoji"
                    onClick={() => setEmojiOpen((prev) => !prev)}
                    className={`icon-muted ${emojiOpen ? "is-active" : ""}`}
                  >
                    <EmojiEmotionsOutlinedIcon />
                  </IconButton>
                </Box>
                <Typography className="chat-widget-count">
                  {message.length}/200
                </Typography>
              </Box>
              {emojiOpen && (
                <Box className="chat-widget-emoji-picker">
                  {TEAM_EMOJIS.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      className="chat-widget-emoji"
                      onClick={() => handleEmojiSelect(emoji)}
                    >
                      {emoji}
                    </button>
                  ))}
                </Box>
              )}
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                multiple
                className="chat-widget-hidden-input"
                onChange={(event) => {
                  handleFileUpload(event.target.files);
                  if (event.target.value) event.target.value = "";
                }}
              />
              <input
                ref={audioInputRef}
                type="file"
                accept="audio/*"
                multiple
                className="chat-widget-hidden-input"
                onChange={(event) => {
                  handleFileUpload(event.target.files);
                  if (event.target.value) event.target.value = "";
                }}
              />
            </Box>

            {status === "error" && (
              <Typography className="chat-widget-error">{error}</Typography>
            )}

            <Button
              variant="contained"
              className={`chat-widget-send chat-widget-send--${status}`}
              onClick={handleSend}
              disabled={isDisabled || status === "sending" || uploadingFiles || !canSend}
            >
              {status === "sending"
                ? "Sending..."
                : status === "success"
                ? "Sent!"
                : status === "error"
                ? "Try Again"
                : "Send Message"}
            </Button>

            <Typography className="chat-widget-helper">{helperText}</Typography>
          </Box>
        </Slide>
      </Modal>
    </>
  );
};

export default FloatingChatWidget;
